import clsx from "clsx";
import { NavLink, useRouteMatch } from "react-router-dom";

import {
  Link,
  Loader,
  PlaceholderProducts,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import {
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import { OrgsRoute, RootPath } from "@lib/routes";
import { createDashboardLink } from "@lib/utils/navigation.utils";
import {
  useCurrentUser,
  useLearnerDashboardEnabled,
  useProducts,
} from "@queries";
import { useOrganizations } from "@queries/organization";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

import "./organization-list.scss";

export function OrganizationList({ responsiveStyles, className }) {
  const products = useProducts();
  const { url } = useRouteMatch(`${RootPath}${OrgsRoute.path}`);
  const currentUser = useCurrentUser();
  const { organizationCode } = currentUser || {};

  const { data: organizations, isFetching: isFetchingOrganizations } =
    useOrganizations(organizationCode, {
      enabled: !!organizationCode,
    });

  const learnerDashboardEnabled = useLearnerDashboardEnabled();

  const columns = [
    new Column(
      "name",
      "Name",
      ColumnType.LINK,
      new ColumnOptions({
        sortingEnabled: true,
        overflowEnabled: true,
        customFunction: (value, row) => (
          <Link
            routerComponent={NavLink}
            to={createDashboardLink({
              orgCode: row.code,
              search: {
                from: "All Organizations",
              },
            })}
          >
            {value}
          </Link>
        ),
      })
    ),
    new Column(
      "totalClassrooms",
      "Classrooms",
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    new Column(
      "totalInstructors",
      "Instructors",
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    new Column(
      "totalTrainees",
      `${getLearnerTerm({ plural: true })}`,
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    new Column(
      "simsCompleted",
      "Sims Completed",
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    new Column(
      "simsMastered",
      "Sims Mastered",
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    ...(products.length > 1
      ? [
          new Column(
            "products",
            "Product",
            ColumnType.CUSTOM,
            new ColumnOptions({
              sortingEnabled: true,
              customFunction: (value) => {
                if (!learnerDashboardEnabled) {
                  return (
                    <PlaceholderProducts
                      products={value.filter(
                        (p) => p !== Product.CE && p !== Product.PROG
                      )}
                    />
                  );
                } else {
                  return (
                    <PlaceholderProducts
                      products={value.filter((p) => p !== Product.CE)}
                    />
                  );
                }
              },
            })
          ),
        ]
      : []),
  ];

  const pageHeader = (
    <PageHeader
      title={"Organizations"}
      badge={isFetchingOrganizations ? "..." : organizations?.length}
    ></PageHeader>
  );

  return (
    <PageLayout
      className={clsx("organizations-list-page", className)}
      header={pageHeader}
      organizationFilterEnabled={false}
      responsiveStyles={responsiveStyles}
    >
      {isFetchingOrganizations && <Loader fixed />}
      <DataTable
        columns={columns}
        data={organizations}
        className={clsx(
          "organization-list-data-table",
          products.length > 1 && "multiple-products"
        )}
        keyFields={["code"]}
        defaultSortPropName="name"
        defaultSortDirection={SortDirection.ASCENDING}
        responsiveStyles={responsiveStyles}
        keyPrefix="organization"
        rowHeightConfig={{ large: 54 }}
        virtualRowEnabled
      />
    </PageLayout>
  );
}
